import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateValidationError } from "@mui/x-date-pickers/models";
import { AxiosError } from "axios";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { createReprintAuth, revokeReprintAuth } from "../../services";
import {
  ReprintBillTarget,
  AllowedCopy,
  ReprintGrantConflict,
} from "../../types/reprintAuth.type";
import { PrintCopyType } from "../../types/customer.type";
import { COPY_TYPES, getCopyTypes } from "../Customer/Details/printCopyTypes";
import { CHARCOAL, TRANSACTION_TYPE_LABEL } from "./data";

interface CreateGrantModalProps {
  open: boolean;
  bill: ReprintBillTarget | null;
  onClose: () => void;
  onCreated: () => void;
}

type ExpiryMode = "1h" | "3h" | "date";

const MIN_QTY = 1;
const MAX_QTY = 9;

// Allowed copy quantities keyed by type. A key present means the copy type is
// selected. Defaults to every type the shop prints (all four, or C + A for an e-Tax
// shop) selected at quantity 1 (matches the design).
type CopySelection = Partial<Record<PrintCopyType, number>>;

const defaultSelection = (isEtaxShop: boolean): CopySelection =>
  getCopyTypes(isEtaxShop).reduce<CopySelection>(
    (selection, copyType) => ({ ...selection, [copyType.key]: MIN_QTY }),
    {},
  );

// Selection → API `allowed_copies`, emitted in fixed A→D order.
const toAllowedCopies = (sel: CopySelection): AllowedCopy[] =>
  COPY_TYPES.filter((t) => sel[t.key] != null).map((t) => ({
    type: t.key,
    quantity: sel[t.key] as number,
  }));

// Resolve the selected expiry into a concrete valid_until (or null if invalid).
// Label and computed value now agree: 1h → now+1h, 3h → now+3h, date → 23:59.
const resolveValidUntil = (
  mode: ExpiryMode,
  date: Dayjs | null,
): Dayjs | null => {
  if (mode === "1h") return dayjs().add(1, "hour");
  if (mode === "3h") return dayjs().add(3, "hour");
  if (date && date.isValid()) return date.endOf("day"); // 23:59:59 local
  return null;
};

// What the blocking grant still owes, as «C 1 ใบ · D 1 ใบ». Empty when it carries no
// per-type restriction (legacy grants), where "how much is left" has no answer.
const remainingText = (conflict: ReprintGrantConflict): string =>
  COPY_TYPES.map((t) => {
    const remaining = conflict.usage_by_type?.[t.key]?.remaining ?? 0;
    return remaining > 0 ? `${t.key} ${remaining} ใบ` : "";
  })
    .filter(Boolean)
    .join(" · ");

export default function CreateGrantModal({
  open,
  bill,
  onClose,
  onCreated,
}: CreateGrantModalProps) {
  const [mode, setMode] = useState<ExpiryMode>("3h");
  const [date, setDate] = useState<Dayjs | null>(null);
  const [dateError, setDateError] = useState<DateValidationError>(null);
  const [reason, setReason] = useState<string>("");
  const isEtaxShop = bill?.is_etax_shop === true;
  const copyTypes = getCopyTypes(isEtaxShop);
  const [sel, setSel] = useState<CopySelection>(() => defaultSelection(isEtaxShop));
  const [loading, setLoading] = useState<boolean>(false);
  // The blocking grant, when the bill already carries one with quota to spare.
  const [conflict, setConflict] = useState<ReprintGrantConflict | null>(null);
  // Any other failure — the request suppresses the global toast, so without this the
  // modal would just sit there looking like nothing happened.
  const [failed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setMode("3h");
      setDate(null);
      setDateError(null);
      setReason("");
      setSel(defaultSelection(isEtaxShop));
      setConflict(null);
      setFailed(false);
    }
  }, [open, isEtaxShop]);

  const handleToggle = (key: PrintCopyType) =>
    setSel((prev) => {
      const next = { ...prev };
      if (next[key] != null) delete next[key];
      else next[key] = MIN_QTY;
      return next;
    });

  const handleBump = (key: PrintCopyType, delta: number) =>
    setSel((prev) => {
      const cur = prev[key] ?? 0;
      const clamped = Math.max(MIN_QTY, Math.min(MAX_QTY, cur + delta));
      return { ...prev, [key]: clamped };
    });

  const validUntil = useMemo(
    () => resolveValidUntil(mode, date),
    [mode, date],
  );

  const selectedCount = COPY_TYPES.filter((t) => sel[t.key] != null).length;

  // Submit is allowed only when the resolved expiry is valid AND in the future,
  // and at least one copy type is selected.
  const isFuture = Boolean(validUntil && validUntil.isAfter(dayjs()));
  const canSubmit = isFuture && selectedCount > 0 && !dateError && !loading;

  // The modal owns its own failure reporting (the request suppresses the global toast),
  // because the one failure that matters here needs more than a sentence: which grant is
  // in the way, what is still unprinted on it, and a way out.
  const submitCreate = async () => {
    if (!bill || !validUntil || selectedCount === 0) return;
    setLoading(true);
    setConflict(null);
    setFailed(false);
    try {
      await createReprintAuth({
        transaction_uuid: bill.transaction_uuid,
        transaction_type: bill.transaction_type,
        valid_until: validUntil.toISOString(),
        reason: reason.trim() || undefined,
        allowed_copies: toAllowedCopies(sel),
      });
      onCreated();
      onClose();
    } catch (error) {
      const response = (error as AxiosError<{ error?: ReprintGrantConflict }>).response;
      // 409 WITHOUT a payload can only come from a backend older than this contract —
      // fall back to the generic message rather than rendering a blank conflict box.
      if (response?.status === 409 && response.data?.error?.grant_uuid) {
        setConflict(response.data.error);
      } else {
        setFailed(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // «ยกเลิกสิทธิ์เดิมแล้วสร้างใหม่». Revoking is what the back office was already doing by
  // hand between the error and the retry — 70 times on prod — so do both in one tap.
  const handleRevokeAndRetry = async () => {
    if (!conflict) return;
    setLoading(true);
    try {
      await revokeReprintAuth(conflict.grant_uuid);
    } catch {
      // The revoke request is not toast-suppressed, so the global handler has already
      // said what went wrong — adding the modal's own generic line would only repeat it.
      setLoading(false);
      return;
    }
    setLoading(false);
    await submitCreate();
  };

  const resolvedText = validUntil
    ? `หมดอายุ ${validUntil.format("DD/MM/YYYY HH:mm")} น.`
    : "กรุณาเลือกวันที่";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, color: CHARCOAL, pr: 6 }}>
        สร้างสิทธิ์พิมพ์ซ้ำ
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {bill && (
          <Box
            sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f6f6f9", mb: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {bill.transaction_no}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {TRANSACTION_TYPE_LABEL[bill.transaction_type]} ·{" "}
              {bill.customer_name} ({bill.customer_code})
            </Typography>
          </Box>
        )}

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          ประเภทและจำนวนที่อนุญาตให้พิมพ์ซ้ำ
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 2,
            py: 0.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 0.5,
            }}
          >
            <Box sx={{ width: 42, flexShrink: 0 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ flex: 1 }}
            >
              ประเภทบิลที่อนุญาตให้พิมพ์ซ้ำ
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ width: 132, textAlign: "center" }}
            >
              จำนวน (ใบ)
            </Typography>
          </Box>

          {copyTypes.map((t) => {
            const checked = sel[t.key] != null;
            const qty = sel[t.key] ?? 0;
            return (
              <Box
                key={t.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 0.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Checkbox
                  checked={checked}
                  onChange={() => handleToggle(t.key)}
                  size="small"
                  sx={{ p: 0, width: 42, flexShrink: 0 }}
                />
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    {t.key}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.label}
                  </Typography>
                </Box>
                {checked ? (
                  <Box
                    sx={{
                      width: 132,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconButton
                      aria-label="ลด"
                      size="small"
                      disabled={qty <= MIN_QTY}
                      onClick={() => handleBump(t.key, -1)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "8px 0 0 8px",
                        color: "primary.main",
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Box
                      sx={{
                        minWidth: 44,
                        height: 34,
                        px: 1,
                        borderTop: "1px solid",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                      }}
                    >
                      {qty}
                    </Box>
                    <IconButton
                      aria-label="เพิ่ม"
                      size="small"
                      disabled={qty >= MAX_QTY}
                      onClick={() => handleBump(t.key, 1)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "0 8px 8px 0",
                        color: "primary.main",
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ width: 132, textAlign: "center" }}
                  >
                    ไม่อนุญาต
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          ระยะเวลาหมดอายุ
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          fullWidth
          size="small"
          onChange={(_, value: ExpiryMode | null) => {
            if (value) setMode(value);
          }}
          sx={{ mb: 1 }}
        >
          <ToggleButton value="1h">ภายใน 1 ชั่วโมง</ToggleButton>
          <ToggleButton value="3h">ภายใน 3 ชั่วโมง</ToggleButton>
          <ToggleButton value="date">ภายในวันที่กำหนด</ToggleButton>
        </ToggleButtonGroup>

        <Stack gap={1.5} sx={{ mt: 1 }}>
          {mode === "date" && (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
              <DatePicker
                label="วันที่หมดอายุ"
                value={date}
                format="DD/MM/YYYY"
                disablePast
                onChange={(value) => setDate(value)}
                onError={(error) => setDateError(error)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: Boolean(dateError),
                  },
                }}
              />
            </LocalizationProvider>
          )}

          <Typography variant="caption" color={isFuture ? "text.secondary" : "error"}>
            {resolvedText}
          </Typography>

          <TextField
            label="เหตุผล (ไม่บังคับ)"
            placeholder="ระบุเหตุผลในการเปิดสิทธิ์"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
          />
          {conflict ? (
            <Alert severity="warning">
              <AlertTitle sx={{ fontWeight: 700 }}>
                บิลนี้มีสิทธิ์พิมพ์ซ้ำที่ยังใช้ไม่หมด
              </AlertTitle>
              {remainingText(conflict)
                ? `ยังพิมพ์ได้อีก ${remainingText(conflict)} · หมดอายุ ${dayjs(
                    conflict.valid_until,
                  ).format("DD/MM/YYYY HH:mm")} น.`
                : `สิทธิ์เดิมหมดอายุ ${dayjs(conflict.valid_until).format(
                    "DD/MM/YYYY HH:mm",
                  )} น.`}
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                ให้พนักงานพิมพ์จากสิทธิ์เดิมก่อน
                หรือยกเลิกสิทธิ์เดิมแล้วสร้างใหม่แทน
              </Typography>
            </Alert>
          ) : null}

          {failed ? (
            <Alert severity="error">
              สร้างสิทธิ์พิมพ์ซ้ำไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          ยกเลิก
        </Button>
        {conflict ? (
          <Button onClick={handleRevokeAndRetry} variant="contained" loading={loading}>
            ยกเลิกสิทธิ์เดิมแล้วสร้างใหม่
          </Button>
        ) : (
          <Button
            onClick={submitCreate}
            variant="contained"
            disabled={!canSubmit}
            loading={loading}
          >
            สร้างสิทธิ์
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
